import React, { useMemo } from 'react';
import { defaultSearchParams, queryLetter, templateSuffix } from '../../configs';
import { FACET_KEY_NAMES, FACET_KEY_PIECES, FACET_KEY_ROOMS, FACET_KEY_SHAPES, FACET_KEY_TYPES, } from '../../constants/facetKeys';
import { SEARCH_PARAM_KEY_PAGE_NUMBER, SEARCH_PARAM_KEY_SORT, SEARCH_PARAM_KEY_TYPE } from '../../constants/searchParamKeys';
import { PER_ROW_LESS } from '../../constants/perRowKeys';
import { useAlgoliaDataContext } from '../../hooks/useAlgoliaDataContext';
import { useParams, useSearchParams } from 'react-router-dom';
import { ProductCardVNode } from '../../../Components/react/ProductCardVNode';
import { useLastViewedElement } from '../../../hooks/useLastViewedElement';
import { algoliaClickHit } from '../../events/algolia.clickHit';
import { MEDIA_BREAKPOINTS } from '../../../constants/MEDIA_BREAKPOINTS';
import { PromoCard } from './PromoCard';
import { useDynamicConfigs } from '../../hooks/useDynamicConfigs';
export const getImageSizes = (perRow) => {
    const isLess = perRow === PER_ROW_LESS;
    if (MEDIA_BREAKPOINTS.XXL_UP.matches) {
        return isLess
            ? '23.2088rem'
            : '16.7813rem';
    }
    if (MEDIA_BREAKPOINTS.XL_UP.matches) {
        return isLess
            ? 'calc(24.8124vw - 0.5833rem)'
            : 'calc(17.9470vw - 0.4531rem)';
    }
    if (MEDIA_BREAKPOINTS.LG_UP.matches) {
        return isLess
            ? 'calc(30.7292vw - 0.5833rem)'
            : 'calc(22.0703vw - 0.4375rem)';
    }
    if (MEDIA_BREAKPOINTS.MD_UP.matches) {
        return isLess
            ? 'calc(48.9583vw - 0.875rem)'
            : 'calc(31.9444vw - 0.5833rem)';
    }
    return isLess
        ? 'calc(100vw - 1.75rem)'
        : 'calc(47.8667vw - 0.875rem)';
};
export const ItemsComponent = ({ perRow }) => {
    const [searchParams] = useSearchParams();
    const [lastViewedID, lastViewedRef, setLastViewedFunction] = useLastViewedElement('collectionLastID');
    const { defaultFacets } = useDynamicConfigs();
    const { hierarchyObjects, collection } = useDynamicConfigs();
    const { hits, queryID } = useAlgoliaDataContext();
    const { handle } = useParams();
    const colObject = hierarchyObjects?.[handle ?? ''];
    const nbPage = +(searchParams.get(SEARCH_PARAM_KEY_PAGE_NUMBER) ?? 1);
    const query = searchParams.get(queryLetter);
    const hidePromoCard = (collection?.capsuleItems !== undefined && collection.capsuleItems.length > 1) ||
        handle === 'photo-prints';
    const perRowClass = perRow === PER_ROW_LESS
        ? 'gtc:1 gtc:2:md-up gtc:3:lg-up'
        : 'gtc:2 gtc:3:md-up gtc:4:lg-up';
    const imageSizes = getImageSizes(perRow);
    const currentFilters = useMemo(() => {
        const type = searchParams.get(SEARCH_PARAM_KEY_TYPE);
        const shapes = searchParams.get(FACET_KEY_SHAPES);
        const pieces = searchParams.get(FACET_KEY_PIECES);
        const rooms = searchParams.get(FACET_KEY_ROOMS);
        const names = searchParams.get(FACET_KEY_NAMES);
        return {
            shapes: [
                ...(shapes?.split(',') ?? []),
                ...(defaultFacets?.[FACET_KEY_SHAPES] ?? [])
            ],
            pieces: [
                ...(pieces?.split(',') ?? []),
                ...(defaultFacets?.[FACET_KEY_PIECES] ?? [])
            ],
            rooms: [
                ...(rooms?.split(',') ?? []),
                ...(defaultFacets?.[FACET_KEY_ROOMS] ?? [])
            ],
            names: [
                ...(names?.split(',') ?? []),
                ...(defaultFacets?.[FACET_KEY_NAMES] ?? [])
            ],
            types: [
                ...(type !== null ? [type] : []),
                ...(defaultFacets?.[FACET_KEY_TYPES] ?? [])
            ]
        };
    }, [hits]);
    const index = searchParams.get(SEARCH_PARAM_KEY_SORT) ?? defaultSearchParams.index;
    const collectionName = colObject?.fullName ?? collection?.title ?? 'Search';
    const hierarchyPath = colObject?.facetValue ?? colObject?.fullName ?? collection?.title ?? 'Search';
    const pageNum = +(searchParams.get(SEARCH_PARAM_KEY_PAGE_NUMBER) ?? 1);
    return (React.createElement("ul", { "data-cy-test": 'CollectionItemsList', className: `list:none d:grid rgap:20 cgap:16 mb:40 rgap:28:lg-up cgap:36:lg-up ${perRowClass}` },
        hits.map((hit, i) => (React.createElement("li", { key: hit.objectID, ...hit.objectID === lastViewedID && { ref: lastViewedRef }, style: { order: i + 1 } },
            React.createElement(ProductCardVNode, { hit: hit, filters: currentFilters, imageSizes: imageSizes, clickHandler: () => {
                    setLastViewedFunction(hit.objectID);
                    algoliaClickHit({
                        index,
                        position: ++i * pageNum,
                        collectionName,
                        collectionType: templateSuffix,
                        hierarchyPath,
                        query,
                        hit,
                        queryID
                    });
                } })))),
        !hidePromoCard && nbPage === 1 && hits.length > 8 &&
            React.createElement(PromoCard, { order: 6, link: '/products/custom-canvas-photo-prints?variant=21511449444435', imageURL: 'https://cdn.shopify.com/s/files/1/1568/8443/files/custom-prints-wall-art.webp?v=1677144983', perRow: perRow }),
        !hidePromoCard && nbPage === 1 && hits.length > 11 && hits.length < 47 &&
            React.createElement(PromoCard, { order: 11, link: '/products/custom-canvas-photo-prints?variant=21511449346131', imageURL: 'https://cdn.shopify.com/s/files/1/1568/8443/files/canvas-prints-wall-art.webp?v=1677144983', perRow: perRow }),
        !hidePromoCard && nbPage === 2 && hits.length > 8 &&
            React.createElement(PromoCard, { order: 6, link: '/products/custom-canvas-photo-prints?variant=21511449346131', imageURL: 'https://cdn.shopify.com/s/files/1/1568/8443/files/canvas-prints-wall-art.webp?v=1677144983', perRow: perRow })));
};
